import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { AppComponent } from '../../app.component';
import { takeWhile } from 'rxjs/operators';
import { CategoryService } from '../services/category.service';

@Component({
    selector: 'ef-bizprocess-menu',
    templateUrl: './bizprocess-menu.component.html',
    styleUrls: ['./bizprocess-menu.component.scss'],
    standalone: false
})
export class BizProcessMenuComponent implements OnInit, OnDestroy {
    @Input() isMainPage: boolean = false;

    categoryMenuItems: MenuItem[] = [];
    selectedCategoryId: number | null = null;
    private active = true;

    constructor(
        public app: AppComponent,
        private categoryService: CategoryService
    ) {}

    ngOnInit() {
        this.setupSubscriptions();
    }

    ngOnDestroy() {
        this.active = false;
    }

    private setupSubscriptions() {
        // Subscribe to selected category changes
        this.categoryService.selectedCategoryId$
            .pipe(takeWhile(_ => this.active))
            .subscribe(selectedCategoryId => {
                this.selectedCategoryId = selectedCategoryId;
                this.updateMenuItemStyles();
            });

        this.categoryService.categories$
            .pipe(takeWhile(_ => this.active))
            .subscribe(categories => {
                // Filter menu items to only show "All Categories" and categories with IsBizProcess: true
                const bizProcessCategories = categories.filter(
                    category => category.IsBizProcess
                );

                this.categoryMenuItems = [
                    {
                        label: 'Show All',
                        icon: 'pi pi-home',
                        tooltipOptions: {
                            tooltipLabel: 'Show All Process Categories',
                            tooltipPosition: 'bottom' as const,
                            tooltipStyleClass: 'tooltip-fit-content',
                            showDelay: 300,
                            hideDelay: 300
                        },
                        command: () => this.filterByCategory(null),
                        id: 'show-all'
                    },
                    ...bizProcessCategories.map(category => ({
                        label: category.CategoryName,
                        icon: 'pi pi-cog',
                        tooltipOptions: {
                            tooltipLabel: category.LongDescription || category.CategoryName,
                            tooltipPosition: 'bottom' as const,
                            tooltipStyleClass: 'tooltip-fit-content',
                            showDelay: 300,
                            hideDelay: 300
                        },
                        command: () =>
                            this.filterByCategory(category.CategoryId),
                        id: `category-${category.CategoryId}`
                    }))
                ];

                // Apply command wrapping if not on main page
                if (!this.isMainPage) {
                    this.wrapMenuItemCommands();
                }

                // Update styles after menu items are created
                this.updateMenuItemStyles();
            });
    }

    private filterByCategory(categoryId: number | null) {
        if (this.isMainPage) {
            // Direct filtering if on main page
            this.categoryService.filterByCategory(categoryId);
        } else {
            // Navigate to main page first, then filter
            const homeProcess = {
                ProcessID: 0,
                ProcessName: 'Main',
                RouteName: 'main',
                ProcessDesc: '',
                ProcessTitle: '',
                MsgCount: null
            };

            this.app.goto(homeProcess);

            setTimeout(() => {
                this.categoryService.filterByCategory(categoryId);
            }, 100);
        }
    }

    private updateMenuItemStyles() {
        this.categoryMenuItems.forEach(item => {
            const isShowAllItem = item.id === 'show-all';
            const isAnotherItemSelected = this.selectedCategoryId !== null;
            
            // Determine if this item should be selected
            const isSelected = (isShowAllItem && this.selectedCategoryId === null) || 
                             (!isShowAllItem && item.id === `category-${this.selectedCategoryId}`);

            if (isSelected) {
                // Add selected class, keep existing classes
                const existingClasses = item.styleClass || '';
                if (!existingClasses.includes('selected-menu-item')) {
                    item.styleClass = existingClasses ? `${existingClasses} selected-menu-item` : 'selected-menu-item';
                }
            } else {
                // Remove selected class if present
                if (item.styleClass) {
                    item.styleClass = item.styleClass.replace(/\s*selected-menu-item\s*/g, '').trim();
                    if (!item.styleClass) {
                        delete item.styleClass;
                    }
                }
            }

            // Handle Show All item highlight mode
            if (isShowAllItem) {
                let classes = item.styleClass || '';
                
                if (isAnotherItemSelected) {
                    // Another item is selected, add highlight-mode to Show All
                    if (!classes.includes('highlight-mode')) {
                        classes = classes ? `${classes} highlight-mode` : 'highlight-mode';
                    }
                } else {
                    // Show All is selected or no selection, remove highlight-mode
                    classes = classes.replace(/\s*highlight-mode\s*/g, '').trim();
                }
                
                item.styleClass = classes || undefined;
            }
        });
    }

    private wrapMenuItemCommands() {
        this.categoryMenuItems = this.categoryMenuItems.map(item => ({
            ...item,
            command: this.wrapCommand(item.command)
        }));
    }

    private wrapCommand(originalCommand?: (...args: any[]) => void) {
        return (...args: any[]) => {
            const homeProcess = {
                ProcessID: 0,
                ProcessName: 'Main',
                RouteName: 'main',
                ProcessDesc: '',
                ProcessTitle: '',
                MsgCount: null
            };

            this.app.goto(homeProcess);

            setTimeout(() => {
                if (originalCommand) {
                    originalCommand(...args);
                }
            }, 100);
        };
    }
}
